// Behavioral Narrative Generator - AI-powered insights from response patterns
import { FEATURE_FLAGS } from '../config/featureFlags.js';

// Generate comprehensive behavioral narrative
export const generateBehavioralNarrative = (answers, timeSpent, categories, performanceData = {}) => {
  if (!FEATURE_FLAGS.ADVANCED_SCORING) {
    return {
      strengthSummary: [],
      growthOpportunities: [],
      recommendations: [],
      narratives: []
    };
  }

  const responsePatterns = analyzeResponsePatterns(answers, timeSpent);
  const consistencyMetrics = calculateConsistency(answers, categories);
  const engagementMetrics = analyzeEngagement(timeSpent, answers);

  return {
    strengthSummary: generateStrengthSummary(responsePatterns, consistencyMetrics),
    growthOpportunities: identifyGrowthOpportunities(responsePatterns, performanceData),
    recommendations: generateRecommendations(responsePatterns, engagementMetrics),
    narratives: generateNarratives(responsePatterns, consistencyMetrics, engagementMetrics)
  };
};

// Analyze response patterns across all questions
const analyzeResponsePatterns = (answers, timeSpent) => {
  const patterns = {
    responseSpeed: calculateResponseSpeed(timeSpent),
    extremeResponses: countExtremeResponses(answers),
    neutralTendency: calculateNeutralTendency(answers),
    consistencyScore: calculateResponseConsistency(answers),
    thoughtfulness: calculateThoughtfulness(timeSpent, answers)
  };

  return patterns;
};

// Calculate average response speed
const calculateResponseSpeed = (timeSpent) => {
  const times = Object.values(timeSpent).filter(t => t > 0);
  if (times.length === 0) return 0;
  
  const avgTime = times.reduce((sum, time) => sum + time, 0) / times.length;
  const medianTime = times.sort((a, b) => a - b)[Math.floor(times.length / 2)];
  
  return {
    average: Math.round(avgTime / 1000), // Convert to seconds
    median: Math.round(medianTime / 1000),
    variance: calculateVariance(times),
    classification: classifyResponseSpeed(avgTime)
  };
};

// Count extreme responses (1s and 5s for Likert scales)
const countExtremeResponses = (answers) => {
  const responses = Object.values(answers);
  const extremes = responses.filter(r => r === 1 || r === 5).length;
  const total = responses.length;
  
  return {
    count: extremes,
    percentage: total > 0 ? Math.round((extremes / total) * 100) : 0,
    tendency: extremes / total > 0.3 ? 'high' : extremes / total > 0.1 ? 'moderate' : 'low'
  };
};

// Calculate neutral response tendency
const calculateNeutralTendency = (answers) => {
  const responses = Object.values(answers);
  const neutrals = responses.filter(r => r === 3).length;
  const total = responses.length;
  
  return {
    count: neutrals,
    percentage: total > 0 ? Math.round((neutrals / total) * 100) : 0,
    tendency: neutrals / total > 0.4 ? 'high' : neutrals / total > 0.2 ? 'moderate' : 'low'
  };
};

// Calculate response consistency within categories
const calculateResponseConsistency = (answers) => {
  // Simplified consistency check - would need category mapping for full implementation
  const responses = Object.values(answers);
  if (responses.length < 3) return 0;
  
  const variance = calculateVariance(responses);
  const consistency = Math.max(0, 100 - (variance * 20)); // Normalize to 0-100
  
  return Math.round(consistency);
};

// Calculate thoughtfulness based on time and response patterns
const calculateThoughtfulness = (timeSpent, answers) => {
  const times = Object.values(timeSpent);
  const responses = Object.values(answers);
  
  if (times.length === 0) return 0;
  
  const avgTime = times.reduce((sum, time) => sum + time, 0) / times.length;
  const hasVariedResponses = new Set(responses).size > 2;
  
  // Thoughtfulness = adequate time + response variety
  const timeScore = Math.min(100, (avgTime / 10000) * 100); // 10 seconds = 100%
  const varietyScore = hasVariedResponses ? 100 : 50;
  
  return Math.round((timeScore * 0.6 + varietyScore * 0.4));
};

// Calculate variance for array of numbers
const calculateVariance = (values) => {
  if (values.length === 0) return 0;
  
  const mean = values.reduce((sum, val) => sum + val, 0) / values.length;
  const squaredDiffs = values.map(val => Math.pow(val - mean, 2));
  
  return squaredDiffs.reduce((sum, diff) => sum + diff, 0) / values.length;
};

// Classify response speed
const classifyResponseSpeed = (avgTime) => {
  const seconds = avgTime / 1000;
  if (seconds < 3) return 'very_fast';
  if (seconds < 8) return 'fast';
  if (seconds < 15) return 'moderate';
  if (seconds < 25) return 'deliberate';
  return 'very_deliberate';
};

// Calculate consistency metrics across categories
const calculateConsistency = (answers, categories) => {
  // Simplified - would need full category analysis
  return {
    overall: calculateResponseConsistency(answers),
    trend: 'stable', // Would calculate improvement/decline
    reliability: Math.min(100, Object.keys(answers).length * 2) // More answers = higher reliability
  };
};

// Analyze engagement patterns
const analyzeEngagement = (timeSpent, answers) => {
  const responseSpeed = calculateResponseSpeed(timeSpent);
  const completionRate = Object.keys(answers).length; // Simplified
  
  return {
    level: responseSpeed.classification === 'very_fast' ? 'low' : 
           responseSpeed.classification === 'moderate' ? 'high' : 'moderate',
    consistency: responseSpeed.variance < 5000 ? 'high' : 'moderate',
    depth: calculateThoughtfulness(timeSpent, answers) > 70 ? 'high' : 'moderate'
  };
};

// Generate strength summary
const generateStrengthSummary = (patterns, consistency) => {
  const strengths = [];
  
  if (patterns.thoughtfulness > 70) {
    strengths.push('Demonstrates thoughtful consideration in responses');
  }
  
  if (consistency.overall > 80) {
    strengths.push('Shows high consistency in assessment approach');
  }
  
  if (patterns.responseSpeed.classification === 'moderate' || patterns.responseSpeed.classification === 'deliberate') {
    strengths.push('Takes appropriate time for reflection');
  }
  
  if (patterns.extremeResponses.tendency === 'low') {
    strengths.push('Provides balanced, nuanced responses');
  }
  
  if (patterns.neutralTendency.tendency === 'low') {
    strengths.push('Shows clear preferences and opinions');
  }
  
  return strengths.slice(0, 3); // Top 3 strengths
};

// Identify growth opportunities
const identifyGrowthOpportunities = (patterns, performanceData) => {
  const opportunities = [];
  
  if (patterns.neutralTendency.tendency === 'high') {
    opportunities.push('Consider developing stronger preferences and decision-making confidence');
  }
  
  if (patterns.responseSpeed.classification === 'very_fast') {
    opportunities.push('Allow more time for reflection to provide deeper insights');
  }
  
  if (patterns.consistencyScore < 60) {
    opportunities.push('Focus on developing more consistent response patterns');
  }
  
  if (patterns.extremeResponses.tendency === 'high') {
    opportunities.push('Practice providing more nuanced, balanced perspectives');
  }
  
  return opportunities.slice(0, 2); // Top 2 opportunities
};

// Generate actionable recommendations
const generateRecommendations = (patterns, engagement) => {
  const recommendations = [];
  
  if (engagement.level === 'low') {
    recommendations.push('Consider retaking the assessment when you have more focused time available');
  }
  
  if (patterns.thoughtfulness < 50) {
    recommendations.push('Take time to reflect on each question before responding');
  }
  
  if (patterns.consistencyScore > 80) {
    recommendations.push('Your consistent approach suggests reliable self-awareness');
  }
  
  recommendations.push('Review your results with a mentor or coach for deeper insights');
  
  return recommendations;
};

// Generate behavioral narratives
const generateNarratives = (patterns, consistency, engagement) => {
  const narratives = [];
  
  // Response style narrative
  if (patterns.responseSpeed.classification === 'deliberate') {
    narratives.push('Your responses indicate a thoughtful, deliberate approach to decision-making. You tend to carefully consider options before committing to an answer.');
  } else if (patterns.responseSpeed.classification === 'fast') {
    narratives.push('Your responses suggest quick decision-making abilities and confidence in your initial judgments.');
  }
  
  // Consistency narrative
  if (consistency.overall > 80) {
    narratives.push('Your response patterns demonstrate high internal consistency, suggesting strong self-awareness and reliable assessment engagement.');
  }
  
  // Engagement narrative
  if (engagement.depth === 'high') {
    narratives.push('Your engagement with the assessment indicates genuine interest in self-discovery and professional development.');
  }
  
  // Response tendency narrative
  if (patterns.neutralTendency.tendency === 'low' && patterns.extremeResponses.tendency === 'low') {
    narratives.push('Your balanced response style suggests nuanced thinking and the ability to see multiple perspectives.');
  }
  
  return narratives;
};

export default { generateBehavioralNarrative };